import { Component, OnInit, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { ApiResult } from '../../../../shared/models/api-result.model';
import {
  ProductIdentifierType,
  CreateProductIdentifierType,
  UpdateProductIdentifierType,
} from '../../../models/product-identifier-type.model';
import { ProductIdentifierTypeService } from '../../../services/product-identifier-type.service';
import { FormGroupService } from '../../../../shared/services/form-group.service';
import { MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { FormErrorComponent } from '../../../../shared/components/form-error/form-error.component';
import { FormServerErrorComponent } from '../../../../shared/components/form-server-error/form-server-error.component';
import { ToolbarModule } from "primeng/toolbar";

@Component({
  selector: 'app-product-identifier-type-create-or-update',
  imports: [
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    ProgressSpinnerModule,
    ToastModule,
    RouterLink,
    FormErrorComponent,
    FormServerErrorComponent,
    ToolbarModule
],
  templateUrl: './product-identifier-type-create-or-update.component.html',
})
export class ProductIdentifierTypeCreateOrUpdateComponent implements OnInit {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private messageService = inject(MessageService);
  private productIdentifierTypeService = inject(ProductIdentifierTypeService);
  private formGroupService = inject(FormGroupService);

  formGroup: FormGroup = this.fb.group({
    id: [null],
    name: [null, [Validators.required, Validators.maxLength(255)]],
    description: [null, [Validators.maxLength(255)]],
    isActive: [true],
  });

  productIdentifierTypeId = signal<string | null>(null);
  loading = signal(false);

  ngOnInit(): void {
    const id = this.activatedRoute.snapshot.paramMap.get('id');

    if (id) {
      this.productIdentifierTypeId.set(id);
      this.loadData();
    }
  }

  loadData(): void {
    const id = this.productIdentifierTypeId();
    if (!id) return;

    this.loading.set(true);

    this.productIdentifierTypeService.getById(id).subscribe({
      next: (response: ApiResult) => {
        this.loading.set(false);

        const model = response.data as ProductIdentifierType;
        this.formGroup.patchValue(model);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          this.messageService.add({
            severity: 'error',
            summary: 'Falha',
            detail: 'Ocorreu um erro ao carregar o tipo de identificador',
            life: 3000,
          });
        }
      },
    });
  }

  onSubmit(): void {
    if (!this.formGroup.valid) {
      Object.keys(this.formGroup.controls).forEach((key) => {
        this.formGroup.get(key)?.markAsTouched();
      });
      return;
    }

    const id = this.productIdentifierTypeId();
    const body = this.formGroup.getRawValue();

    this.loading.set(true);

    let request$: Observable<ApiResult>;

    if (id) {
      request$ = this.productIdentifierTypeService.update(
        id,
        body as UpdateProductIdentifierType
      );
    } else {
      delete (body as any).id;
      delete (body as any).isActive; // created as active by backend OR not allowed on create
      request$ = this.productIdentifierTypeService.create(
        body as CreateProductIdentifierType
      );
    }

    request$.subscribe({
      next: () => {
        this.loading.set(false);

        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Tipo de identificador salvo com sucesso',
          life: 2000,
        });

        this.router.navigate(['/product-identifier-types']);
      },
      error: (e) => {
        this.loading.set(false);

        if (e instanceof HttpErrorResponse) {
          if ([400, 422].includes(e.status)) {
            if (
              (e.error.message &&
                (e.error.errors || e.error.errors?.length > 0)) ||
              (!e.error.message &&
                (e.error.errors || e.error.errors?.length > 0))
            ) {
              this.formGroupService.addServerErrors(
                e.error.errors,
                this.formGroup
              );
            } else if (
              e.error.message &&
              (!e.error.errors || e.error.errors?.length == 0)
            ) {
              this.messageService.add({
                severity: 'warn',
                summary: 'Atenção',
                detail: e.error.message,
                life: 3000,
              });
            }
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Falha',
              detail: 'Ocorreu um erro ao salvar o tipo de identificador',
              life: 3000,
            });
          }
        }
      },
    });
  }
}
